import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from './useToast';
import {
  getISOWeekNumber as getISOWeekNumberUtil,
  calculateWeeklyOvertime,
  resolveWeeklyHours
} from '../utils/overtimeUtils';
import { getZoneRank } from '../utils/zoneUtils';
import { formatDateLocal } from '../utils/dateUtils';
import type {
  WorkType, TabType, FilterType, FilterMode, HistoryViewMode,
  ProjectFinancials, MonthlyHistoryPoint, ProjectHistory,
  ReportRow, ProjectStats, RawEntry, VentilationRaw,
  AnalyticsData
} from '../pages/reporting/types';
import {
  MONTHS_FR, getISOWeekNumber, getWeekStartDate, getWeekEndDate
} from '../pages/reporting/types';

export function useReportingData() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<TabType>('analytics');
  const [loading, setLoading] = useState(false);

  // --- ÉTATS DU FILTRE DE PÉRIODE UNIFIÉ ---
  const [filterMode, setFilterMode] = useState<FilterMode>('month');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedWeek, setSelectedWeek] = useState(getISOWeekNumber(new Date()));

  // --- FILTRES ANALYTICS ---
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [selectedEmployee, setSelectedEmployee] = useState<string>('all');

  // --- FILTRE RENTABILITÉ ---
  const [rentabiliteFilter, setRentabiliteFilter] = useState<FilterType>('active');
  const [rentaSearch, setRentaSearch] = useState('');

  // --- ÉTATS HISTORIQUE AFFAIRES ---
  const [historyProject, setHistoryProject] = useState<string>('');
  const [projectHistory, setProjectHistory] = useState<ProjectHistory | null>(null);
  const [projectFinancials, setProjectFinancials] = useState<ProjectFinancials | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyViewMode, setHistoryViewMode] = useState<HistoryViewMode>('monthly');
  const [showCumulative, setShowCumulative] = useState(false);
  // Édition inline du montant devis
  const [editingDevis, setEditingDevis] = useState(false);
  const [devisInputValue, setDevisInputValue] = useState('');
  const [savingDevis, setSavingDevis] = useState(false);

  // Listes pour filtres
  const [projectList, setProjectList] = useState<{ id: string, code: string, name: string, category?: string }[]>([]);
  const [employeeList, setEmployeeList] = useState<{ id: string, name: string }[]>([]);

  // États Données
  const [payrollData, setPayrollData] = useState<ReportRow[]>([]);
  const [projectStats, setProjectStats] = useState<ProjectStats[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    global: { etude: 0, suivi: 0, mise_en_service: 0, chantier: 0 },
    byProject: {},
    byEmployee: {},
    totalHours: 0,
    totalEmployees: 0,
    avgHoursPerEmployee: 0,
    gdDaysCount: 0
  });

  // --- CALCUL DES DATES SELON LE MODE ---
  // Pour l'arrêté mensuel, on calcule aussi une période étendue couvrant les semaines complètes
  // afin de calculer correctement les HS sur les semaines chevauchantes
  const { startDate, endDate, extendedStartDate, extendedEndDate, periodLabel, dbPeriodStr } = useMemo(() => {
    let start: Date, end: Date, label: string, dbStr: string;
    let extStart: Date, extEnd: Date;

    if (filterMode === 'week') {
      start = getWeekStartDate(selectedYear, selectedWeek);
      end = getWeekEndDate(start);
      extStart = start;
      extEnd = end;
      label = `Semaine ${selectedWeek} (${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })})`;
      dbStr = `S${selectedWeek}_${selectedYear}`;
    } else if (filterMode === 'month') {
      start = new Date(selectedYear, selectedMonth, 1);
      end = new Date(selectedYear, selectedMonth + 1, 0);
      label = `${MONTHS_FR[selectedMonth]} ${selectedYear}`;
      dbStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;

      // Période étendue : début de la semaine du 1er jour, fin de la semaine du dernier jour
      const firstDayWeek = getISOWeekNumber(start);
      const firstDayYear = start.getFullYear();
      // Si le 1er janvier est en semaine 52/53 de l'année précédente
      const adjustedFirstYear = (firstDayWeek > 50 && start.getMonth() === 0) ? firstDayYear - 1 : firstDayYear;
      extStart = getWeekStartDate(adjustedFirstYear, firstDayWeek);

      const lastDayWeek = getISOWeekNumber(end);
      const lastDayYear = end.getFullYear();
      // Si le 31 décembre est en semaine 1 de l'année suivante
      const adjustedLastYear = (lastDayWeek === 1 && end.getMonth() === 11) ? lastDayYear + 1 : lastDayYear;
      extEnd = getWeekEndDate(getWeekStartDate(adjustedLastYear, lastDayWeek));
    } else {
      start = new Date(selectedYear, 0, 1);
      end = new Date(selectedYear, 11, 31);
      extStart = start;
      extEnd = end;
      label = `Année ${selectedYear}`;
      dbStr = `${selectedYear}`;
    }

    return {
      startDate: formatDateLocal(start),
      endDate: formatDateLocal(end),
      extendedStartDate: formatDateLocal(extStart),
      extendedEndDate: formatDateLocal(extEnd),
      periodLabel: label,
      dbPeriodStr: dbStr
    };
  }, [filterMode, selectedYear, selectedMonth, selectedWeek]);

  // --- NAVIGATION TEMPORELLE ---
  const handlePrevious = () => {
    if (filterMode === 'week') {
      if (selectedWeek > 1) setSelectedWeek(selectedWeek - 1);
      else { setSelectedYear(selectedYear - 1); setSelectedWeek(52); }
    } else if (filterMode === 'month') {
      if (selectedMonth > 0) setSelectedMonth(selectedMonth - 1);
      else { setSelectedYear(selectedYear - 1); setSelectedMonth(11); }
    } else {
      setSelectedYear(selectedYear - 1);
    }
  };

  const handleNext = () => {
    if (filterMode === 'week') {
      if (selectedWeek < 52) setSelectedWeek(selectedWeek + 1);
      else { setSelectedYear(selectedYear + 1); setSelectedWeek(1); }
    } else if (filterMode === 'month') {
      if (selectedMonth < 11) setSelectedMonth(selectedMonth + 1);
      else { setSelectedYear(selectedYear + 1); setSelectedMonth(0); }
    } else {
      setSelectedYear(selectedYear + 1);
    }
  };

  const handleToday = () => {
    const now = new Date();
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth());
    setSelectedWeek(getISOWeekNumber(now));
  };

  // --- CHARGEMENT DES LISTES ---
  useEffect(() => {
    const loadLists = async () => {
      const { data: projs } = await supabase.from('projects').select('id, code, name, category').order('code');
      if (projs) setProjectList(projs);
      const { data: emps } = await supabase.from('employees').select('id, full_name').order('full_name');
      if (emps) setEmployeeList(emps.map(e => ({ id: e.id, name: e.full_name })));
    };
    loadLists();
  }, []);

  // --- 1. CHARGEMENT ANALYTICS ---
  const fetchAnalytics = useCallback(async () => {
    if (projectList.length === 0) return;

    setLoading(true);
    try {
      const query = supabase
        .from('time_entries')
        .select(`
          id, project_id, work_date, hours_normal, hours_saturday, hours_major, is_grand_deplacement,
          weekly_sheets!inner ( status, employee:employees!employee_id ( id, full_name ) )
        `)
        .eq('weekly_sheets.status', 'validated')
        .gte('work_date', startDate)
        .lte('work_date', endDate);

      const { data: entriesData, error } = await query;
      if (error) throw error;

      const entries = (entriesData as unknown as RawEntry[]) || [];
      const entryIds = entries.map(e => e.id);

      let ventilations: VentilationRaw[] = [];
      if (entryIds.length > 0) {
        const { data: ventsData } = await supabase
          .from('be_ventilations')
          .select('*')
          .in('time_entry_id', entryIds);
        ventilations = (ventsData as unknown as VentilationRaw[]) || [];
      }

      const ventsMap: Record<string, VentilationRaw[]> = {};
      ventilations.forEach(v => {
        if (!ventsMap[v.time_entry_id]) ventsMap[v.time_entry_id] = [];
        ventsMap[v.time_entry_id].push(v);
      });

      const stats: AnalyticsData = {
        global: { etude: 0, suivi: 0, mise_en_service: 0, chantier: 0 },
        byProject: {},
        byEmployee: {},
        totalHours: 0,
        totalEmployees: 0,
        avgHoursPerEmployee: 0,
        gdDaysCount: 0
      };

      const employeeSet = new Set<string>();
      const processedGDDates = new Set<string>(); // Pour éviter les doublons de jours GD

      entries.forEach(entry => {
        if (selectedProject !== 'all' && entry.project_id !== selectedProject) return;
        if (selectedEmployee !== 'all' && entry.weekly_sheets?.employee?.id !== selectedEmployee) return;

        const pId = entry.project_id;
        const projInfo = projectList.find(p => p.id === pId) || { code: '?', name: 'Inconnu' };
        const emp = entry.weekly_sheets?.employee;

        if (!stats.byProject[pId]) {
          stats.byProject[pId] = {
            name: projInfo.name,
            code: projInfo.code,
            etude: 0, suivi: 0, mise_en_service: 0, chantier: 0, total: 0
          };
        }

        if (emp) {
          employeeSet.add(emp.id);
          if (!stats.byEmployee[emp.id]) {
            stats.byEmployee[emp.id] = { name: emp.full_name, total: 0 };
          }
        }

        const entryTotal = (entry.hours_normal || 0) + (entry.hours_saturday || 0) + (entry.hours_major || 0);
        const entryVents = ventsMap[entry.id];

        if (entryVents && entryVents.length > 0) {
          entryVents.forEach(v => {
            if (stats.global[v.work_type] !== undefined) {
              stats.global[v.work_type] += v.hours;
              stats.byProject[pId][v.work_type] += v.hours;
              stats.byProject[pId].total += v.hours;
            }
          });
          if (emp) stats.byEmployee[emp.id].total += entryTotal;
        } else {
          stats.global.chantier += entryTotal;
          stats.byProject[pId].chantier += entryTotal;
          stats.byProject[pId].total += entryTotal;
          if (emp) stats.byEmployee[emp.id].total += entryTotal;
        }

        stats.totalHours += entryTotal;

        // Comptage des jours GD (éviter les doublons par date+employé)
        if (entry.is_grand_deplacement && emp) {
          const gdKey = `${emp.id}_${entry.work_date}`;
          if (!processedGDDates.has(gdKey)) {
            processedGDDates.add(gdKey);
            stats.gdDaysCount++;
          }
        }
      });

      stats.totalEmployees = employeeSet.size;
      stats.avgHoursPerEmployee = stats.totalEmployees > 0 ? stats.totalHours / stats.totalEmployees : 0;

      setAnalytics(stats);

    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedProject, selectedEmployee, projectList]);

  // --- 2. CHARGEMENT PAIE ---
  const fetchPayroll = useCallback(async () => {
    setLoading(true);
    try {
      // En mode mensuel, on charge la période étendue pour calculer correctement les HS
      // sur les semaines chevauchant la fin/début de mois
      const queryStartDate = filterMode === 'month' ? extendedStartDate : startDate;
      const queryEndDate = filterMode === 'month' ? extendedEndDate : endDate;

      const { data } = await supabase
        .from('time_entries')
        .select(`
          hours_normal, hours_saturday, hours_major,
          meals_count, travel_zone, indemnity_transport, indemnity_trajet, work_date,
          is_grand_deplacement, gd_amount, gd_travel_hours,
          weekly_sheets!inner ( status, year, week_number, override_weekly_hours, employee:employees!employee_id ( id, full_name, role, custom_weekly_hours ) )
        `)
        .eq('weekly_sheets.status', 'validated')
        .gte('work_date', queryStartDate)
        .lte('work_date', queryEndDate);

      const aggregation: Record<string, ReportRow> = {};
      const processedDates = new Set<string>();
      const processedGDDates = new Set<string>(); // Pour dédoublonner les jours GD
      const entriesByEmpAndDay: Record<string, Record<string, { hours: number; zone: string | number }[]>> = {};
      // Structure pour calculer les HS par semaine pour chaque employé (période étendue)
      // Format: { empId: { "2024-W01": { normal: number, saturday: number, sunday: number, daysInPeriod: number, totalDays: number, hasGD: boolean } } }
      const weeklyHoursByEmp: Record<string, Record<string, { normal: number; saturday: number; sunday: number; daysInPeriod: number; totalDays: number; hasGD: boolean }>> = {};
      // Map des overrides base horaire par employé/semaine
      const overrideMap: Record<string, Record<string, number | null>> = {};

      const entries = (data as unknown as RawEntry[]) || [];

      // Premier passage : collecter toutes les heures par semaine (période étendue)
      entries.forEach(entry => {
        const emp = entry.weekly_sheets?.employee;
        if (!emp) return;

        if (!weeklyHoursByEmp[emp.id]) {
          weeklyHoursByEmp[emp.id] = {};
        }

        const workDate = new Date(entry.work_date);
        const weekNum = getISOWeekNumberUtil(workDate);
        const year = workDate.getFullYear();
        // Gérer le cas où le 31 déc est en semaine 1 de l'année suivante
        const adjustedYear = (weekNum === 1 && workDate.getMonth() === 11) ? year + 1 : year;
        const weekKey = `${adjustedYear}-W${weekNum}`;

        if (!weeklyHoursByEmp[emp.id][weekKey]) {
          weeklyHoursByEmp[emp.id][weekKey] = { normal: 0, saturday: 0, sunday: 0, daysInPeriod: 0, totalDays: 0, hasGD: false };
        }

        // Ajouter les heures à la semaine
        weeklyHoursByEmp[emp.id][weekKey].normal += entry.hours_normal || 0;
        weeklyHoursByEmp[emp.id][weekKey].saturday += entry.hours_saturday || 0;
        weeklyHoursByEmp[emp.id][weekKey].sunday += entry.hours_major || 0;
        weeklyHoursByEmp[emp.id][weekKey].totalDays += 1;
        // Marquer si la semaine contient un Grand Déplacement
        if (entry.is_grand_deplacement) {
          weeklyHoursByEmp[emp.id][weekKey].hasGD = true;
        }

        // Collecter l'override de la feuille pour cette semaine
        const sheetOverride = entry.weekly_sheets?.override_weekly_hours;
        if (sheetOverride != null) {
          if (!overrideMap[emp.id]) overrideMap[emp.id] = {};
          overrideMap[emp.id][weekKey] = sheetOverride;
        }

        // Compter si ce jour est dans la période exacte du mois
        const isInExactPeriod = entry.work_date >= startDate && entry.work_date <= endDate;
        if (isInExactPeriod) {
          weeklyHoursByEmp[emp.id][weekKey].daysInPeriod += 1;
        }
      });

      // Deuxième passage : agréger les données pour la période exacte uniquement
      entries.forEach(entry => {
        const emp = entry.weekly_sheets?.employee;
        if (!emp) return;

        // Ne comptabiliser que les entrées de la période exacte
        const isInExactPeriod = entry.work_date >= startDate && entry.work_date <= endDate;
        if (!isInExactPeriod) return;

        const hours = entry.hours_normal + entry.hours_saturday + entry.hours_major;

        if (!aggregation[emp.id]) {
          aggregation[emp.id] = {
            employeeId: emp.id,
            employeeName: emp.full_name,
            role: emp.role || '',
            customWeeklyHours: emp.custom_weekly_hours ?? null,
            totalHours: 0,
            totalMeals: 0,
            totalTransport: 0,
            totalTrajet: 0,
            zones: { "1A": 0, "1B": 0, "2": 0, "3": 0, "4": 0, "5": 0, "6": 0, "7": 0, "GD": 0 },
            overtime: 0,
            overtime25: 0,
            overtime50: 0,
            sundayHours: 0,
            weeklyDetails: [],
            gdDaysCount: 0,
            gdTotalAmount: 0,
            gdTotalTravelHours: 0
          };
          entriesByEmpAndDay[emp.id] = {};
        }

        const row = aggregation[emp.id];
        row.totalHours += hours;

        // Dédoublonnage des indemnités par jour/employé
        // RÈGLE GD : Si jour GD (is_grand_deplacement=true), ne pas compter panier/transport/trajet
        // Le dernier jour GD de la semaine a is_grand_deplacement=false et compte son panier normalement
        const dateKey = `${emp.id}_${entry.work_date}`;
        if (!processedDates.has(dateKey)) {
          if (!entry.is_grand_deplacement) {
            row.totalMeals += entry.meals_count || 0;
            row.totalTransport += (entry.indemnity_transport || 0);
            row.totalTrajet += (entry.indemnity_trajet || 0);
          }
          processedDates.add(dateKey);
        }

        // Dédoublonnage et comptage des Grands Déplacements par jour/employé
        const gdDateKey = `gd_${emp.id}_${entry.work_date}`;
        if (entry.is_grand_deplacement && !processedGDDates.has(gdDateKey)) {
          row.gdDaysCount += 1;
          row.gdTotalAmount += entry.gd_amount || 0;
          row.gdTotalTravelHours += entry.gd_travel_hours || 0;
          processedGDDates.add(gdDateKey);

          // Ajouter également ce jour à la zone GD pour cohérence avec la colonne "Zone GD"
          if (!entriesByEmpAndDay[emp.id][entry.work_date]) {
            entriesByEmpAndDay[emp.id][entry.work_date] = [];
          }
          // Ajouter une entrée GD avec les heures du jour pour garantir que GD "gagne" le calcul de zone
          entriesByEmpAndDay[emp.id][entry.work_date].push({ hours: 999, zone: 'GD' });
        }

        // Collecter les zones avec leurs heures pour calcul de la zone gagnante par jour
        // (sauf si déjà traité comme GD ci-dessus)
        // RÈGLE : Exclure aussi les jours avec travel_zone='GD' si is_grand_deplacement=false
        // (c'est le dernier jour GD qui a panier repas, il ne doit pas être compté comme zone GD)
        const zoneStr = entry.travel_zone?.toString().toUpperCase();
        const isLastGDDay = !entry.is_grand_deplacement && zoneStr === 'GD';
        if (!entry.is_grand_deplacement && !isLastGDDay && entry.travel_zone && entry.travel_zone !== 0 && entry.travel_zone !== '0' && hours > 0) {
          if (!entriesByEmpAndDay[emp.id][entry.work_date]) {
            entriesByEmpAndDay[emp.id][entry.work_date] = [];
          }
          entriesByEmpAndDay[emp.id][entry.work_date].push({ hours, zone: entry.travel_zone });
        }
      });

      // Calculer les heures supplémentaires et zones pour chaque employé
      Object.values(aggregation).forEach(r => {
        // Calcul des HS par semaine (règles paire/impaire) avec prorata pour semaines chevauchantes
        const empWeekly = weeklyHoursByEmp[r.employeeId];
        if (empWeekly) {
          Object.entries(empWeekly).forEach(([weekKey, weekData]) => {
            // Ignorer les semaines sans jours dans la période exacte
            if (weekData.daysInPeriod === 0) return;

            const [yearStr, weekStr] = weekKey.split('-W');
            const year = parseInt(yearStr);
            const weekNum = parseInt(weekStr);

            const hoursLuSa = weekData.normal + weekData.saturday;
            const isBE35hReport = r.role === 'bureau_etude';
            const effectiveHours = resolveWeeklyHours(overrideMap[r.employeeId]?.[weekKey], r.customWeeklyHours);
            const breakdown = calculateWeeklyOvertime(weekNum, hoursLuSa, weekData.sunday, weekData.hasGD || isBE35hReport, effectiveHours);

            // Calculer le ratio pour les semaines chevauchantes
            const ratio = weekData.totalDays > 0 ? weekData.daysInPeriod / weekData.totalDays : 1;

            // Appliquer le ratio aux HS (pour arrêté mensuel correct)
            r.overtime25 += breakdown.overtime25 * ratio;
            r.overtime50 += breakdown.overtime50 * ratio;
            r.sundayHours += breakdown.sundayHours * ratio;

            r.weeklyDetails.push({
              ...breakdown,
              overtime25: breakdown.overtime25 * ratio,
              overtime50: breakdown.overtime50 * ratio,
              sundayHours: breakdown.sundayHours * ratio,
              weekNumber: weekNum,
              year
            });
          });
        }

        // Total des HS (pour rétro-compatibilité)
        r.overtime = r.overtime25 + r.overtime50;

        // Calcul des zones gagnantes par jour
        const empDays = entriesByEmpAndDay[r.employeeId];
        if (empDays) {
          Object.values(empDays).forEach(dayEntries => {
            if (dayEntries.length === 0) return;

            dayEntries.sort((a, b) => {
              if (b.hours !== a.hours) return b.hours - a.hours;
              return getZoneRank(b.zone) - getZoneRank(a.zone);
            });

            const winningZone = dayEntries[0].zone.toString();
            r.zones[winningZone] = (r.zones[winningZone] || 0) + 1;
          });
        }

        // Arrondir les valeurs pour éviter les erreurs de virgule flottante
        r.totalHours = parseFloat(r.totalHours.toFixed(2));
        r.overtime = parseFloat(r.overtime.toFixed(2));
        r.overtime25 = parseFloat(r.overtime25.toFixed(2));
        r.overtime50 = parseFloat(r.overtime50.toFixed(2));
        r.sundayHours = parseFloat(r.sundayHours.toFixed(2));
        r.gdTotalAmount = parseFloat(r.gdTotalAmount.toFixed(2));
        r.gdTotalTravelHours = parseFloat(r.gdTotalTravelHours.toFixed(2));
      });

      // ── INJECTION SECRÉTARIAT (heures automatiques 32h/sem + vendredis extras) ──
      const { data: secEmployees } = await supabase
        .from('employees')
        .select('id, full_name, role, custom_weekly_hours')
        .eq('role', 'secretariat')
        .eq('is_active', true);

      if (secEmployees && secEmployees.length > 0) {
        const { data: secExtraHours } = await supabase
          .from('secretariat_extra_hours')
          .select('employee_id, work_date, hours')
          .gte('work_date', startDate)
          .lte('work_date', endDate);

        for (const sec of secEmployees) {
          if (aggregation[sec.id]) continue; // déjà présent via time_entries

          // Calculer par semaine ISO (identique aux autres employés)
          // Pour chaque jour de la période, on regroupe par semaine ISO
          const weekMap: Record<string, { daysInPeriod: number; totalWeekDays: number; fridayHours: number }> = {};
          const pStart = new Date(startDate);
          const pEnd = new Date(endDate);

          // Parcourir chaque jour Lu-Ve de la période
          const d = new Date(pStart);
          while (d <= pEnd) {
            const dow = d.getDay();
            if (dow >= 1 && dow <= 5) { // lundi à vendredi
              const wn = getISOWeekNumberUtil(d);
              const yr = d.getFullYear();
              const wk = `${yr}-W${wn}`;
              if (!weekMap[wk]) weekMap[wk] = { daysInPeriod: 0, totalWeekDays: 0, fridayHours: 0 };
              if (dow <= 4) weekMap[wk].daysInPeriod++; // Lu-Je comptent comme base
            }
            d.setDate(d.getDate() + 1);
          }

          // Compter les jours totaux Lu-Je de chaque semaine (pour le prorata)
          for (const wk of Object.keys(weekMap)) {
            const [yr, wPart] = wk.split('-W');
            const jan4 = new Date(parseInt(yr), 0, 4);
            const dayOfWeek = jan4.getDay() || 7;
            const monday = new Date(jan4);
            monday.setDate(jan4.getDate() - dayOfWeek + 1 + (parseInt(wPart) - 1) * 7);
            let total = 0;
            for (let i = 0; i < 4; i++) { // Lu-Je = 4 jours
              const day = new Date(monday);
              day.setDate(monday.getDate() + i);
              if (day.getDay() >= 1 && day.getDay() <= 4) total++;
            }
            weekMap[wk].totalWeekDays = total;
          }

          // Heures vendredis par semaine
          const extras = (secExtraHours || []).filter(
            (ex: { employee_id: string }) => ex.employee_id === sec.id
          );
          extras.forEach((ex: { work_date: string; hours: number }) => {
            const exDate = new Date(ex.work_date);
            const wn = getISOWeekNumberUtil(exDate);
            const yr = exDate.getFullYear();
            const wk = `${yr}-W${wn}`;
            if (!weekMap[wk]) weekMap[wk] = { daysInPeriod: 0, totalWeekDays: 4, fridayHours: 0 };
            weekMap[wk].fridayHours += (ex.hours || 0);
          });

          // Base mensuelle contractuelle (mensualisée) : 32h × 52 / 12 = 138.67h
          const baseHours = parseFloat((32 * 52 / 12).toFixed(2)); // 138.67
          let fridayTotal = 0;
          let ot25 = 0, ot50 = 0;

          Object.values(weekMap).forEach(wk => {
            const ratio = wk.totalWeekDays > 0 ? wk.daysInPeriod / wk.totalWeekDays : 1;
            fridayTotal += wk.fridayHours;

            // HS si vendredi déclaré cette semaine
            if (wk.fridayHours > 0) {
              const totalWeekHours = 32 + wk.fridayHours;
              const breakdown = calculateWeeklyOvertime(0, totalWeekHours, 0, false, 32);
              ot25 += breakdown.overtime25 * ratio;
              ot50 += breakdown.overtime50 * ratio;
            }
          });

          aggregation[sec.id] = {
            employeeId: sec.id,
            employeeName: sec.full_name,
            role: sec.role,
            customWeeklyHours: 32,
            totalHours: parseFloat((baseHours + fridayTotal).toFixed(2)),
            totalMeals: 0,
            totalTransport: 0,
            totalTrajet: 0,
            zones: {},
            overtime: parseFloat((ot25 + ot50).toFixed(2)),
            overtime25: parseFloat(ot25.toFixed(2)),
            overtime50: parseFloat(ot50.toFixed(2)),
            sundayHours: 0,
            weeklyDetails: [],
            gdDaysCount: 0,
            gdTotalAmount: 0,
            gdTotalTravelHours: 0
          };
        }
      }

      setPayrollData(Object.values(aggregation));
    } catch (error) { console.error(error); } finally { setLoading(false); }
  }, [startDate, endDate, extendedStartDate, extendedEndDate, filterMode]);

  // --- 3. CHARGEMENT RENTABILITÉ ---
  const fetchProfitability = useCallback(async () => {
    setLoading(true);
    try {
      let projectQuery = supabase.from('projects').select('*');

      if (rentabiliteFilter === 'active' || rentabiliteFilter === 'risque') {
        // Risque = filtre côté client après chargement des actifs
        projectQuery = projectQuery.eq('is_active', true);
      } else if (rentabiliteFilter === 'archived') {
        projectQuery = projectQuery.eq('is_active', false);
      }

      // Exclure le projet formation/stage (SYS-FORM) et les catégories divers de l'affichage rentabilité
      projectQuery = projectQuery.neq('code', 'SYS-FORM');

      const { data: projects, error: projError } = await projectQuery.order('code', { ascending: false });
      if (projError) throw projError;
      if (!projects) return;

      // Filtrer les projets divers (catégorie 'divers') côté client
      const productionProjects = projects.filter(p => (p.category ?? 'production') !== 'divers');
      const projectIds = productionProjects.map(p => p.id);

      if (projectIds.length === 0) {
        setProjectStats([]);
        return;
      }

      // Requête heures validées (avec work_date et is_grand_deplacement pour calcul HS)
      const { data: entries, error: entriesErr } = await supabase
        .from('time_entries')
        .select(`
            id, project_id, hours_normal, hours_saturday, hours_major,
            work_date, is_grand_deplacement,
            weekly_sheets!inner( id, status, employee_id, year, week_number, override_weekly_hours ),
            be_ventilations (work_type, hours)
        `)
        .eq('weekly_sheets.status', 'validated')
        .in('project_id', projectIds);

      if (entriesErr) throw entriesErr;

      // Récupérer les taux horaires + rôle + base horaire des employés (requête séparée)
      const employeeIds = [...new Set((entries || []).map((e: { weekly_sheets: { employee_id: string }[] }) => e.weekly_sheets?.[0]?.employee_id).filter(Boolean))];
      const empDataMap: Record<string, { rate: number; role: string; customWeeklyHours: number | null }> = {};
      if (employeeIds.length > 0) {
        const { data: empRates } = await supabase
          .from('employees')
          .select('id, hourly_rate, role, custom_weekly_hours')
          .in('id', employeeIds);
        (empRates || []).forEach((emp: { id: string; hourly_rate: number | null; role: string; custom_weekly_hours: number | null }) => {
          empDataMap[emp.id] = {
            rate: emp.hourly_rate ?? 0,
            role: emp.role ?? '',
            customWeeklyHours: emp.custom_weekly_hours ?? null
          };
        });
      }

      // Agréger heures + coût MO par projet
      const statsMap: Record<string, { ch: number; be: number; moCost: number }> = {};

      type RentaEntry = RawEntry & {
        weekly_sheets: { id: string; status: string; employee_id: string; year?: number; week_number?: number; override_weekly_hours?: number | null } | null;
        work_date: string;
        is_grand_deplacement?: boolean;
      };
      const typedEntries = (entries || []) as unknown as RentaEntry[];

      // --- Calcul MO pondéré avec majorations HS ---
      // Étape 1 : Regrouper les entrées par employé+semaine pour calculer les HS
      const weeklyByEmp: Record<string, Record<string, {
        normal: number; saturday: number; sunday: number; hasGD: boolean;
        projectHours: Record<string, { normal: number; saturday: number; sunday: number }>;
      }>> = {};
      // Map des overrides pour la section Rentabilité
      const rentaOverrideMap: Record<string, Record<string, number | null>> = {};

      typedEntries.forEach(e => {
        const empId = e.weekly_sheets?.employee_id;
        if (!empId) return;

        const workDate = new Date(e.work_date);
        const weekNum = getISOWeekNumberUtil(workDate);
        const year = workDate.getFullYear();
        const adjustedYear = (weekNum === 1 && workDate.getMonth() === 11) ? year + 1 : year;
        const weekKey = `${adjustedYear}-W${weekNum}`;

        if (!weeklyByEmp[empId]) weeklyByEmp[empId] = {};
        if (!weeklyByEmp[empId][weekKey]) {
          weeklyByEmp[empId][weekKey] = { normal: 0, saturday: 0, sunday: 0, hasGD: false, projectHours: {} };
        }

        const w = weeklyByEmp[empId][weekKey];
        w.normal += e.hours_normal || 0;
        w.saturday += e.hours_saturday || 0;
        w.sunday += e.hours_major || 0;
        if (e.is_grand_deplacement) w.hasGD = true;

        // Collecter l'override de la feuille
        const sheetOverride = e.weekly_sheets?.override_weekly_hours;
        if (sheetOverride != null) {
          if (!rentaOverrideMap[empId]) rentaOverrideMap[empId] = {};
          rentaOverrideMap[empId][weekKey] = sheetOverride;
        }

        // Ventiler les heures par projet pour répartir le coût MO
        const pId = e.project_id;
        if (!w.projectHours[pId]) w.projectHours[pId] = { normal: 0, saturday: 0, sunday: 0 };
        w.projectHours[pId].normal += e.hours_normal || 0;
        w.projectHours[pId].saturday += e.hours_saturday || 0;
        w.projectHours[pId].sunday += e.hours_major || 0;
      });

      // Étape 2 : Calculer les HS par semaine et répartir le coût pondéré par projet
      Object.entries(weeklyByEmp).forEach(([empId, weeks]) => {
        const empInfo = empDataMap[empId];
        const rate = empInfo?.rate ?? 0;
        if (rate === 0) return; // Pas de taux horaire, pas de coût

        const empRole = empInfo?.role ?? '';
        const customHours = empInfo?.customWeeklyHours ?? null;

        Object.entries(weeks).forEach(([weekKey, weekData]) => {
          const [, weekStr] = weekKey.split('-W');
          const weekNum = parseInt(weekStr);

          const hoursLuSa = weekData.normal + weekData.saturday;
          const isBE35hReport = empRole === 'bureau_etude';
          const effectiveHoursRenta = resolveWeeklyHours(rentaOverrideMap[empId]?.[weekKey], customHours);
          const breakdown = calculateWeeklyOvertime(weekNum, hoursLuSa, weekData.sunday, weekData.hasGD || isBE35hReport, effectiveHoursRenta);

          // Coût pondéré total de la semaine
          const weekCost = breakdown.regularHours * rate
            + breakdown.overtime25 * rate * 1.25
            + breakdown.overtime50 * rate * 1.50
            + breakdown.sundayHours * rate * 2.0;

          // Répartir le coût au prorata des heures travaillées par projet
          const totalWeekHours = hoursLuSa + weekData.sunday;
          if (totalWeekHours === 0) return;

          Object.entries(weekData.projectHours).forEach(([pId, ph]) => {
            const projectTotalHours = ph.normal + ph.saturday + ph.sunday;
            const ratio = projectTotalHours / totalWeekHours;
            if (!statsMap[pId]) statsMap[pId] = { ch: 0, be: 0, moCost: 0 };
            statsMap[pId].moCost += weekCost * ratio;
          });
        });
      });

      // Étape 3 : Agréger les heures par type (chantier/BE) par projet
      typedEntries.forEach(e => {
        const totalEntry = (e.hours_normal || 0) + (e.hours_saturday || 0) + (e.hours_major || 0);
        const pId = e.project_id;
        if (!statsMap[pId]) statsMap[pId] = { ch: 0, be: 0, moCost: 0 };

        if (e.be_ventilations && e.be_ventilations.length > 0) {
          let entryBE = 0;
          let entryChantier = 0;
          e.be_ventilations.forEach(v => {
            if (v.work_type === 'etude' || v.work_type === 'suivi') entryBE += v.hours;
            else entryChantier += v.hours;
          });
          statsMap[pId].be += entryBE;
          statsMap[pId].ch += entryChantier;
        } else {
          statsMap[pId].ch += totalEntry;
        }
      });

      // Requête achats validés par projet (batch)
      const { data: achatsData } = await supabase
        .from('purchases')
        .select('project_id, total_ht')
        .in('project_id', projectIds)
        .eq('status', 'validated');

      const achatsMap: Record<string, number> = {};
      (achatsData || []).forEach((a: { project_id: string; total_ht: number | null }) => {
        achatsMap[a.project_id] = (achatsMap[a.project_id] || 0) + (a.total_ht || 0);
      });

      // Requête factures clients envoyées/payées par projet
      const { data: facturesData } = await supabase
        .from('invoices')
        .select('project_id, total_ht')
        .in('project_id', projectIds)
        .in('status', ['sent', 'paid']);

      const facturesMap: Record<string, number> = {};
      (facturesData || []).forEach((f: { project_id: string | null; total_ht: number | null }) => {
        if (f.project_id) {
          facturesMap[f.project_id] = (facturesMap[f.project_id] || 0) + (f.total_ht || 0);
        }
      });

      const finalStats: ProjectStats[] = productionProjects.map(p => {
        const devis = (p as unknown as { devis_amount?: number | null }).devis_amount ?? null;
        const totalAchats = achatsMap[p.id] || 0;
        const totalMoCost = statsMap[p.id]?.moCost || 0;
        const totalFacture = facturesMap[p.id] || 0;

        // Si des factures existent, utiliser le CA facturé comme revenu réel pour la marge
        const caReel = totalFacture > 0 ? totalFacture : devis;
        const marge = caReel != null ? caReel - totalAchats - totalMoCost : null;
        const margePercent = caReel != null && caReel > 0 ? (marge! / caReel) * 100 : null;

        return {
          id: p.id,
          code: p.code,
          name: p.name,
          isActive: p.is_active,
          budgetChantier: p.estimated_hours || 0,
          budgetBE: p.estimated_hours_be || 0,
          consumedChantier: statsMap[p.id]?.ch || 0,
          consumedBE: statsMap[p.id]?.be || 0,
          devisAmount: devis,
          totalAchats,
          totalMoCost,
          marge,
          margePercent,
          totalFacture,
          resteAFacturer: devis != null ? devis - totalFacture : null,
        };
      });

      setProjectStats(finalStats);

    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [rentabiliteFilter]);

  // --- 4. CHARGEMENT HISTORIQUE AFFAIRE ---
  const fetchProjectHistory = useCallback(async (projectId: string) => {
    if (!projectId) return;

    setHistoryLoading(true);
    try {
      // 1. Récupérer les détails du projet (avec devis_amount)
      const { data: project, error: projError } = await supabase
        .from('projects')
        .select('id, code, name, created_at, is_active, estimated_hours, estimated_hours_be, devis_amount')
        .eq('id', projectId)
        .single();

      if (projError || !project) throw projError || new Error('Projet non trouvé');

      // 2. Récupérer TOUTES les time_entries validées pour ce projet
      const { data: entriesData, error: entriesError } = await supabase
        .from('time_entries')
        .select(`
          id, project_id, work_date, hours_normal, hours_saturday, hours_major,
          weekly_sheets!inner ( status )
        `)
        .eq('project_id', projectId)
        .eq('weekly_sheets.status', 'validated')
        .order('work_date', { ascending: true });

      if (entriesError) throw entriesError;

      const entries = (entriesData || []) as unknown as RawEntry[];
      const entryIds = entries.map(e => e.id);

      // 3. Récupérer les ventilations BE (par lots de 1000 pour les gros projets)
      const ventilations: VentilationRaw[] = [];
      if (entryIds.length > 0) {
        const chunks: string[][] = [];
        for (let i = 0; i < entryIds.length; i += 1000) {
          chunks.push(entryIds.slice(i, i + 1000));
        }

        for (const chunk of chunks) {
          const { data: ventsData } = await supabase
            .from('be_ventilations')
            .select('time_entry_id, work_type, hours')
            .in('time_entry_id', chunk);
          if (ventsData) ventilations.push(...(ventsData as unknown as VentilationRaw[]));
        }
      }

      // Construire la map des ventilations
      const ventsMap: Record<string, VentilationRaw[]> = {};
      ventilations.forEach(v => {
        if (!ventsMap[v.time_entry_id]) ventsMap[v.time_entry_id] = [];
        ventsMap[v.time_entry_id].push(v);
      });

      // 4. Agréger par mois
      const monthlyAgg: Record<string, { etude: number; suivi: number; mise_en_service: number; chantier: number }> = {};

      entries.forEach(entry => {
        const monthKey = entry.work_date.substring(0, 7); // "YYYY-MM"

        if (!monthlyAgg[monthKey]) {
          monthlyAgg[monthKey] = { etude: 0, suivi: 0, mise_en_service: 0, chantier: 0 };
        }

        const entryTotal = (entry.hours_normal || 0) + (entry.hours_saturday || 0) + (entry.hours_major || 0);
        const entryVents = ventsMap[entry.id];

        if (entryVents && entryVents.length > 0) {
          entryVents.forEach(v => {
            const wt = v.work_type as WorkType;
            if (monthlyAgg[monthKey][wt] !== undefined) {
              monthlyAgg[monthKey][wt] += v.hours;
            }
          });
        } else {
          // Par défaut, les heures vont en chantier
          monthlyAgg[monthKey].chantier += entryTotal;
        }
      });

      // 5. Construire le tableau mensuel trié avec cumuls
      const sortedMonths = Object.keys(monthlyAgg).sort();
      let cumulativeTotal = 0;

      const monthlyData: MonthlyHistoryPoint[] = sortedMonths.map(monthKey => {
        const data = monthlyAgg[monthKey];
        const monthTotal = data.etude + data.suivi + data.mise_en_service + data.chantier;
        cumulativeTotal += monthTotal;

        const [year, month] = monthKey.split('-');
        const monthLabel = `${MONTHS_FR[parseInt(month) - 1]} ${year}`;

        return {
          month: monthKey,
          monthLabel,
          ...data,
          total: parseFloat(monthTotal.toFixed(2)),
          cumulative: parseFloat(cumulativeTotal.toFixed(2))
        };
      });

      // 6. Calculer les totaux
      const totalHours = {
        etude: parseFloat(monthlyData.reduce((acc, m) => acc + m.etude, 0).toFixed(2)),
        suivi: parseFloat(monthlyData.reduce((acc, m) => acc + m.suivi, 0).toFixed(2)),
        mise_en_service: parseFloat(monthlyData.reduce((acc, m) => acc + m.mise_en_service, 0).toFixed(2)),
        chantier: parseFloat(monthlyData.reduce((acc, m) => acc + m.chantier, 0).toFixed(2)),
        total: parseFloat(cumulativeTotal.toFixed(2))
      };

      // Fallback pour created_at si null (utiliser la première entrée)
      const projectCreatedAt = project.created_at || (sortedMonths[0] ? `${sortedMonths[0]}-01` : new Date().toISOString());

      // 7. Construire l'objet historique final
      const history: ProjectHistory = {
        projectId: project.id,
        projectCode: project.code,
        projectName: project.name,
        createdAt: projectCreatedAt,
        isActive: project.is_active,
        budgetChantier: project.estimated_hours || 0,
        budgetBE: project.estimated_hours_be || 0,
        monthlyData,
        totalHours,
        progressChantier: project.estimated_hours ? (totalHours.chantier / project.estimated_hours) * 100 : 0,
        progressBE: project.estimated_hours_be ? ((totalHours.etude + totalHours.suivi) / project.estimated_hours_be) * 100 : 0,
        firstEntryDate: sortedMonths[0] || projectCreatedAt.substring(0, 7),
        lastEntryDate: sortedMonths[sortedMonths.length - 1] || projectCreatedAt.substring(0, 7),
        spanMonths: sortedMonths.length
      };

      setProjectHistory(history);

      // --- 8. Calcul des KPIs financiers ---
      // 8a. Total achats validés pour ce projet
      const { data: achatsData } = await supabase
        .from('purchases')
        .select('total_ht')
        .eq('project_id', projectId)
        .eq('status', 'validated');
      const totalAchats = (achatsData || []).reduce((s, p: { total_ht: number | null }) => s + (p.total_ht || 0), 0);

      // 8b. Coût MO = heures validées × taux horaire pondéré avec majorations HS
      let totalMoCost = 0;
      const { data: moRaw, error: moErr } = await supabase
        .from('time_entries')
        .select(`
          hours_normal, hours_saturday, hours_major, work_date, is_grand_deplacement,
          weekly_sheets!inner ( status, employee_id, override_weekly_hours )
        `)
        .eq('project_id', projectId)
        .eq('weekly_sheets.status', 'validated');

      if (moErr) {
        console.warn('[Historique] Erreur requête MO:', moErr.message);
      } else {
        type MoRawEntry = {
          hours_normal: number;
          hours_saturday: number;
          hours_major: number;
          work_date: string;
          is_grand_deplacement?: boolean;
          weekly_sheets: { status: string; employee_id: string; override_weekly_hours?: number | null } | null;
        };
        const moEntries = (moRaw || []) as unknown as MoRawEntry[];
        const moEmpIds = [...new Set(moEntries.map(e => e.weekly_sheets?.employee_id).filter(Boolean))] as string[];

        // Récupérer taux horaires + rôle + base horaire des employés
        const moEmpDataMap: Record<string, { rate: number; role: string; customWeeklyHours: number | null }> = {};
        if (moEmpIds.length > 0) {
          const { data: moEmpRates } = await supabase
            .from('employees')
            .select('id, hourly_rate, role, custom_weekly_hours')
            .in('id', moEmpIds);
          (moEmpRates || []).forEach((emp: { id: string; hourly_rate: number | null; role: string; custom_weekly_hours: number | null }) => {
            moEmpDataMap[emp.id] = {
              rate: emp.hourly_rate ?? 0,
              role: emp.role ?? '',
              customWeeklyHours: emp.custom_weekly_hours ?? null
            };
          });
        }

        // Regrouper par employé+semaine (toutes les heures de la semaine, pas seulement ce projet)
        // Note: on ne peut pas connaître les heures sur d'autres projets ici, mais les HS sont
        // calculées sur la totalité des heures travaillées tous projets confondus.
        // Pour un calcul exact, il faudrait charger TOUTES les time_entries de chaque employé.
        // Ici on charge toutes les entrées validées des mêmes employés pour la même période.
        const allMoEntries: MoRawEntry[] = [];
        if (moEmpIds.length > 0) {
          const { data: allEntriesRaw } = await supabase
            .from('time_entries')
            .select(`
              hours_normal, hours_saturday, hours_major, work_date, is_grand_deplacement, project_id,
              weekly_sheets!inner ( status, employee_id, override_weekly_hours )
            `)
            .eq('weekly_sheets.status', 'validated')
            .in('weekly_sheets.employee_id', moEmpIds);
          if (allEntriesRaw) {
            (allEntriesRaw as unknown as (MoRawEntry & { project_id: string })[]).forEach(e => allMoEntries.push(e));
          }
        }

        // Regrouper par employé+semaine (toutes les heures, tous projets)
        const moWeeklyByEmp: Record<string, Record<string, {
          normal: number; saturday: number; sunday: number; hasGD: boolean;
          projectHours: Record<string, { normal: number; saturday: number; sunday: number }>;
        }>> = {};

        // Map des overrides pour le calcul Historique
        const histOverrideMap: Record<string, Record<string, number | null>> = {};

        (allMoEntries as unknown as (MoRawEntry & { project_id: string })[]).forEach(e => {
          const empId = e.weekly_sheets?.employee_id;
          if (!empId) return;

          const workDate = new Date(e.work_date);
          const weekNum = getISOWeekNumberUtil(workDate);
          const year = workDate.getFullYear();
          const adjustedYear = (weekNum === 1 && workDate.getMonth() === 11) ? year + 1 : year;
          const weekKey = `${adjustedYear}-W${weekNum}`;

          if (!moWeeklyByEmp[empId]) moWeeklyByEmp[empId] = {};
          if (!moWeeklyByEmp[empId][weekKey]) {
            moWeeklyByEmp[empId][weekKey] = { normal: 0, saturday: 0, sunday: 0, hasGD: false, projectHours: {} };
          }

          const w = moWeeklyByEmp[empId][weekKey];
          w.normal += e.hours_normal || 0;
          w.saturday += e.hours_saturday || 0;
          w.sunday += e.hours_major || 0;
          if (e.is_grand_deplacement) w.hasGD = true;

          // Collecter l'override de la feuille
          const sheetOverride = e.weekly_sheets?.override_weekly_hours;
          if (sheetOverride != null) {
            if (!histOverrideMap[empId]) histOverrideMap[empId] = {};
            histOverrideMap[empId][weekKey] = sheetOverride;
          }

          // Ventiler par projet
          const pId = (e as unknown as { project_id: string }).project_id;
          if (!w.projectHours[pId]) w.projectHours[pId] = { normal: 0, saturday: 0, sunday: 0 };
          w.projectHours[pId].normal += e.hours_normal || 0;
          w.projectHours[pId].saturday += e.hours_saturday || 0;
          w.projectHours[pId].sunday += e.hours_major || 0;
        });

        // Calculer le coût MO pondéré pour ce projet uniquement
        Object.entries(moWeeklyByEmp).forEach(([empId, weeks]) => {
          const empInfo = moEmpDataMap[empId];
          const rate = empInfo?.rate ?? 0;
          if (rate === 0) return;

          const empRole = empInfo?.role ?? '';
          const customHours = empInfo?.customWeeklyHours ?? null;

          Object.entries(weeks).forEach(([weekKey, weekData]) => {
            const [, weekStr] = weekKey.split('-W');
            const weekNum = parseInt(weekStr);

            const hoursLuSa = weekData.normal + weekData.saturday;
            const isBE35hReport = empRole === 'bureau_etude';
            const effectiveHoursHist = resolveWeeklyHours(histOverrideMap[empId]?.[weekKey], customHours);
            const breakdown = calculateWeeklyOvertime(weekNum, hoursLuSa, weekData.sunday, weekData.hasGD || isBE35hReport, effectiveHoursHist);

            // Coût pondéré total de la semaine
            const weekCost = breakdown.regularHours * rate
              + breakdown.overtime25 * rate * 1.25
              + breakdown.overtime50 * rate * 1.50
              + breakdown.sundayHours * rate * 2.0;

            // Répartir au prorata des heures de CE projet sur le total de la semaine
            const totalWeekHours = hoursLuSa + weekData.sunday;
            if (totalWeekHours === 0) return;

            const projHours = weekData.projectHours[projectId];
            if (!projHours) return; // Cet employé n'a pas travaillé sur ce projet cette semaine

            const projectTotalHours = projHours.normal + projHours.saturday + projHours.sunday;
            const ratio = projectTotalHours / totalWeekHours;
            totalMoCost += weekCost * ratio;
          });
        });
      }

      // 8c. Calcul de la marge
      const devisAmount: number | null = (project as unknown as { devis_amount?: number | null }).devis_amount ?? null;
      const marge = devisAmount != null ? devisAmount - totalAchats - totalMoCost : null;
      const margePercent = devisAmount != null && devisAmount > 0 ? (marge! / devisAmount) * 100 : null;

      setProjectFinancials({ devisAmount, totalAchats, totalMoCost, marge, margePercent });
      setDevisInputValue(devisAmount != null ? String(devisAmount) : '');

    } catch (error) {
      console.error('Erreur chargement historique projet:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Sauvegarder le devis inline depuis l'onglet Historique
  const handleSaveDevis = async () => {
    if (!historyProject) return;
    setSavingDevis(true);
    try {
      const newAmount = devisInputValue.trim() === '' ? null : parseFloat(devisInputValue);
      const { error } = await supabase
        .from('projects')
        .update({ devis_amount: newAmount })
        .eq('id', historyProject);
      if (error) throw error;
      // Recalculer la marge avec le nouveau montant
      if (projectFinancials) {
        const marge = newAmount != null ? newAmount - projectFinancials.totalAchats - projectFinancials.totalMoCost : null;
        const margePercent = newAmount != null && newAmount > 0 ? (marge! / newAmount) * 100 : null;
        setProjectFinancials({ ...projectFinancials, devisAmount: newAmount, marge, margePercent });
      }
      setEditingDevis(false);
    } catch (err) {
      console.error('Erreur mise à jour devis:', err);
      toast.error('Erreur lors de la mise à jour du devis.');
    } finally {
      setSavingDevis(false);
    }
  };

  // Charger l'historique quand un projet est sélectionné
  useEffect(() => {
    if (activeTab === 'historique' && historyProject) {
      fetchProjectHistory(historyProject);
    } else {
      // Retour vue globale : réinitialiser les données projet
      setProjectHistory(null);
    }
    // Réinitialiser l'édition devis quand on change de projet
    setEditingDevis(false);
    setProjectFinancials(null);
  }, [activeTab, historyProject, fetchProjectHistory]);

  useEffect(() => {
    if (activeTab === 'analytics') fetchAnalytics();
    else if (activeTab === 'rentabilite' || activeTab === 'historique') fetchProfitability();
    else if (activeTab === 'paie') fetchPayroll();
  }, [activeTab, fetchAnalytics, fetchProfitability, fetchPayroll]);

  // --- HELPER: AGRÉGATION ANNUELLE POUR HISTORIQUE ---
  const aggregateByYear = (monthlyData: MonthlyHistoryPoint[]): MonthlyHistoryPoint[] => {
    const yearlyMap: Record<string, MonthlyHistoryPoint> = {};

    monthlyData.forEach(m => {
      const year = m.month.substring(0, 4);
      if (!yearlyMap[year]) {
        yearlyMap[year] = {
          month: year,
          monthLabel: year,
          etude: 0, suivi: 0, mise_en_service: 0, chantier: 0,
          total: 0, cumulative: 0
        };
      }
      yearlyMap[year].etude += m.etude;
      yearlyMap[year].suivi += m.suivi;
      yearlyMap[year].mise_en_service += m.mise_en_service;
      yearlyMap[year].chantier += m.chantier;
      yearlyMap[year].total += m.total;
      yearlyMap[year].cumulative = m.cumulative; // Dernière valeur cumulative de l'année
    });

    return Object.values(yearlyMap).sort((a, b) => a.month.localeCompare(b.month));
  };

  // --- DONNÉES GRAPHIQUE HISTORIQUE ---
  const historyChartData = useMemo(() => {
    if (!projectHistory) return null;

    const data = historyViewMode === 'yearly'
      ? aggregateByYear(projectHistory.monthlyData)
      : projectHistory.monthlyData;

    if (showCumulative) {
      return {
        labels: data.map(d => historyViewMode === 'yearly' ? d.monthLabel : d.monthLabel.split(' ')[0].substring(0, 3)),
        datasets: [{
          label: 'Cumul Total',
          data: data.map(d => d.cumulative),
          borderColor: '#059669',
          backgroundColor: 'rgba(5, 150, 105, 0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 4,
          pointHoverRadius: 6
        }]
      };
    }

    return {
      labels: data.map(d => historyViewMode === 'yearly' ? d.monthLabel : d.monthLabel.split(' ')[0].substring(0, 3)),
      datasets: [
        { label: 'Bureau d\'Étude', data: data.map(d => d.etude), backgroundColor: '#3b82f6', stack: 'stack1' },
        { label: 'Suivi', data: data.map(d => d.suivi), backgroundColor: '#f59e0b', stack: 'stack1' },
        { label: 'Mise en Service', data: data.map(d => d.mise_en_service), backgroundColor: '#8b5cf6', stack: 'stack1' },
        { label: 'Chantier', data: data.map(d => d.chantier), backgroundColor: '#10b981', stack: 'stack1' }
      ]
    };
  }, [projectHistory, historyViewMode, showCumulative]);

  return {
    // Tab & loading
    activeTab, setActiveTab, loading,
    // Period filter
    filterMode, setFilterMode, selectedYear, setSelectedYear,
    selectedMonth, setSelectedMonth, selectedWeek, setSelectedWeek,
    // Period computed values
    startDate, endDate, periodLabel, dbPeriodStr,
    // Navigation
    handlePrevious, handleNext, handleToday,
    // Data
    analytics, payrollData, projectStats, projectHistory, projectFinancials,
    projectList, employeeList,
    // Analytics filters
    selectedProject, setSelectedProject, selectedEmployee, setSelectedEmployee,
    // Rentabilite filters
    rentabiliteFilter, setRentabiliteFilter, rentaSearch, setRentaSearch,
    // History
    historyProject, setHistoryProject, historyLoading,
    historyViewMode, setHistoryViewMode, showCumulative, setShowCumulative,
    editingDevis, setEditingDevis, devisInputValue, setDevisInputValue,
    savingDevis, handleSaveDevis,
    // Reset back to global history view
    resetHistoryView: () => { setHistoryProject(''); setProjectHistory(null); setProjectFinancials(null); },
    // Chart data
    historyChartData,
    // Helpers
    aggregateByYear,
  };
}
